import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Dropdown,
  Option,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Textarea,
  Field,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  FilterRegular,
  ArrowClockwiseRegular,
  SendRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { format, formatDistanceToNow } from 'date-fns';
import DataTable, { type Column } from '../components/DataTable';
import { TableSkeleton } from '../components/LoadingSkeleton';
import {
  getInquiries,
  getInquiryStats,
  replyToInquiry,
  getSystemUsers,
  type InquiryFilters,
} from '../services/api';
import {
  type ClientInquiry,
  type InquiryStatus,
  type InquiryPriority,
  type SystemUser,
  inquiryStatusDisplayNames,
  inquiryStatusColors,
  inquiryPriorityDisplayNames,
  inquiryPriorityColors,
} from '../types';
import InquiryDetailPanel from '../components/InquiryDetailPanel';
import { useInquiriesScopeParams } from './useInquiriesScopeParams';

const statusOptions: InquiryStatus[] = ['new', 'in_progress', 'waiting_for_client', 'resolved', 'closed'];
const priorityOptions: InquiryPriority[] = ['urgent', 'high', 'normal', 'low'];

export default function InquiriesPage() {
  const queryClient = useQueryClient();
  const { status: selectedStatus, priority: selectedPriority, setStatus: setSelectedStatus, setPriority: setSelectedPriority } = useInquiriesScopeParams();
  const [selectedInquiry, setSelectedInquiry] = useState<ClientInquiry | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [quickReplyOpen, setQuickReplyOpen] = useState(false);
  const [quickReplyInquiry, setQuickReplyInquiry] = useState<ClientInquiry | null>(null);
  const [quickReplyText, setQuickReplyText] = useState('');

  // Fetch inquiries
  const filters: InquiryFilters = {
    status: selectedStatus,
    priority: selectedPriority,
  };

  const { data: inquiries, isLoading, error, refetch } = useQuery<ClientInquiry[]>({
    queryKey: ['inquiries', filters],
    queryFn: () => getInquiries(filters),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['inquiryStats'],
    queryFn: getInquiryStats,
    refetchInterval: 30000,
  });

  // Fetch system users for assignment
  const { data: systemUsers } = useQuery<SystemUser[]>({
    queryKey: ['systemUsers'],
    queryFn: getSystemUsers,
  });

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: ({ id, clientId, body }: { id: string; clientId: string; body: string }) =>
      replyToInquiry(id, { clientId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['inquiryStats'] });
      setQuickReplyOpen(false);
      setQuickReplyText('');
      setQuickReplyInquiry(null);
    },
  });

  // Count unread messages for an inquiry
  const getUnreadCount = (inquiry: ClientInquiry): number => {
    return inquiry.messages.filter(m => m.direction === 'inbound' && !m.readAt).length;
  };

  const clearFilters = () => {
    setSelectedStatus(undefined);
    setSelectedPriority(undefined);
  };

  const handleViewInquiry = (inquiry: ClientInquiry) => {
    setSelectedInquiry(inquiry);
    setDetailPanelOpen(true);
  };

  const handleQuickReply = (inquiry: ClientInquiry, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickReplyInquiry(inquiry);
    setQuickReplyOpen(true);
  };

  const handleSendQuickReply = () => {
    if (!quickReplyInquiry || !quickReplyText.trim()) return;
    replyMutation.mutate({
      id: quickReplyInquiry.id,
      clientId: quickReplyInquiry.clientId,
      body: quickReplyText,
    });
  };

  const columns: Column<ClientInquiry>[] = [
    {
      key: 'priority',
      header: '',
      width: '40px',
      render: (inquiry) => (
        <Tooltip content={`${inquiryPriorityDisplayNames[inquiry.priority]} Priority`} relationship="label">
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: inquiryPriorityColors[inquiry.priority],
            }}
          />
        </Tooltip>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      sortable: true,
      render: (inquiry) => {
        const unreadCount = getUnreadCount(inquiry);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PersonRegular />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 500 }}>{inquiry.clientName}</span>
                {unreadCount > 0 && (
                  <Badge appearance="filled" color="danger" size="small">
                    {unreadCount} new
                  </Badge>
                )}
              </div>
              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>{inquiry.clientEmail}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      render: (inquiry) => (
        <div>
          <div style={{ fontWeight: 500 }}>{inquiry.subject}</div>
          {inquiry.messages.length > 0 && (
            <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {inquiry.messages[inquiry.messages.length - 1].bodyPreview || inquiry.messages[inquiry.messages.length - 1].body.substring(0, 100)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (inquiry) => (
        <Badge
          appearance="filled"
          style={{
            backgroundColor: inquiryStatusColors[inquiry.status],
            color: 'white',
          }}
        >
          {inquiryStatusDisplayNames[inquiry.status]}
        </Badge>
      ),
    },
    {
      key: 'assignedToName',
      header: 'Assigned To',
      sortable: true,
      render: (inquiry) => inquiry.assignedToName || '-',
    },
    {
      key: 'lastMessageAt',
      header: 'Last Activity',
      sortable: true,
      getValue: (inquiry) => new Date(inquiry.lastMessageAt).getTime(),
      render: (inquiry) => (
        <Tooltip content={format(new Date(inquiry.lastMessageAt), 'MMM d, yyyy h:mm a')} relationship="label">
          <span>{formatDistanceToNow(new Date(inquiry.lastMessageAt), { addSuffix: true })}</span>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (inquiry) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Tooltip content="View Details" relationship="label">
            <Button
              appearance="subtle"
              icon={<EyeRegular />}
              size="small"
              onClick={() => handleViewInquiry(inquiry)}
            />
          </Tooltip>
          <Tooltip content="Quick Reply" relationship="label">
            <Button
              appearance="subtle"
              icon={<SendRegular />}
              size="small"
              onClick={(e) => handleQuickReply(inquiry, e)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Failed to load inquiries. Please try again.</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Client Inquiries</h2>
        <p>Manage and respond to client questions and requests</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#0078d4' }}>{stats.new}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>New</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#8B5CF6' }}>{stats.inProgress}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>In Progress</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#F59E0B' }}>{stats.waitingForClient}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Waiting</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#EF4444' }}>{stats.unreadMessages}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Unread Messages</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#EF4444' }}>{stats.byPriority.urgent}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Urgent</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="toolbar">
        <div className="filter-group">
          <FilterRegular />
          <Dropdown
            placeholder="Status"
            value={selectedStatus || ''}
            onOptionSelect={(_, data) => setSelectedStatus(data.optionValue as InquiryStatus || undefined)}
            style={{ minWidth: '160px' }}
          >
            <Option text="All Statuses" value="">All Statuses</Option>
            {statusOptions.map((status) => (
              <Option key={status} text={inquiryStatusDisplayNames[status]} value={status}>
                {inquiryStatusDisplayNames[status]}
              </Option>
            ))}
          </Dropdown>

          <Dropdown
            placeholder="Priority"
            value={selectedPriority || ''}
            onOptionSelect={(_, data) => setSelectedPriority(data.optionValue as InquiryPriority || undefined)}
            style={{ minWidth: '140px' }}
          >
            <Option text="All Priorities" value="">All Priorities</Option>
            {priorityOptions.map((priority) => (
              <Option key={priority} text={inquiryPriorityDisplayNames[priority]} value={priority}>
                {inquiryPriorityDisplayNames[priority]}
              </Option>
            ))}
          </Dropdown>
        </div>

        <Button
          appearance="subtle"
          icon={<ArrowClockwiseRegular />}
          onClick={clearFilters}
        >
          Clear Filters
        </Button>

        <Button
          appearance="subtle"
          icon={<ArrowClockwiseRegular />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </div>

      {/* Inquiries Table */}
      <div className="card">
        <DataTable
          data={inquiries || []}
          columns={columns}
          rowKey="id"
          searchable
          searchPlaceholder="Search by client name, email, or subject..."
          searchKeys={['clientName', 'clientEmail', 'subject']}
          onRowClick={handleViewInquiry}
          emptyMessage="No inquiries found."
          paginated
          initialSort={{ column: 'lastMessageAt', direction: 'desc' }}
        />
      </div>

      {/* Detail Panel */}
      {selectedInquiry && (
        <InquiryDetailPanel
          inquiry={selectedInquiry}
          open={detailPanelOpen}
          onOpenChange={setDetailPanelOpen}
          systemUsers={systemUsers}
          onUpdate={() => {
            queryClient.invalidateQueries({ queryKey: ['inquiries'] });
            queryClient.invalidateQueries({ queryKey: ['inquiryStats'] });
          }}
        />
      )}

      {/* Quick Reply Dialog */}
      <Dialog open={quickReplyOpen} onOpenChange={(_, data) => setQuickReplyOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Quick Reply to {quickReplyInquiry?.clientName}</DialogTitle>
            <DialogContent>
              <Field label="Your Reply">
                <Textarea
                  value={quickReplyText}
                  onChange={(_, data) => setQuickReplyText(data.value)}
                  placeholder="Type your reply..."
                  rows={6}
                  style={{ width: '100%' }}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setQuickReplyOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={<SendRegular />}
                onClick={handleSendQuickReply}
                disabled={!quickReplyText.trim() || replyMutation.isPending}
              >
                {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
